import React from 'react';
import styled from 'styled-components';
import { FjFlexContainer, FjFlexItem, FjTooltip, fjButtonTypes } from '../primitives';

import { Button } from '../styled-primitives';
import { formatDate, formatHWADisplay } from '../../utils';
import DeviceNameRenameable from './DeviceNameRenameable';

type ListProps = FjDivProps & {
  disabled: boolean;
};
const List = styled(FjFlexContainer)<ListProps>`
  flex-direction: column;
  justify-content: space-between;
  row-gap: ${({ theme: { grid } }) => `${grid.units(0.5)}`};
  align-content: stretch;
  padding: ${({ theme: { grid } }) => `${grid.units(0.5)} 0`};
  margin: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.notSelectable};
  overflow: auto;
  color: ${(props) =>
    props.disabled ? props.theme.colors.tabBackground : props.theme.colors.text};
  height: 280px;
`;

const ListItem = styled(FjFlexContainer)<FjDivProps>`
  padding: ${({ theme: { grid } }) => `0 ${grid.units(0.5)}`};
  justify-content: space-between;

  &:nth-child(even) {
    background-color: ${({ theme }) => theme.colors.rowStripe};
  }
`;

const ListItemBlock = styled(FjFlexContainer)`
  flex-direction: column;
  justify-content: flex-start;
`;

const ListItemCell = styled(FjFlexItem)<FjDivProps>`
  justify-content: space-between;
  align-items: top;
`;

const ListItemButtonCell = styled(FjFlexItem)`
  justify-content: flex-start;
  flex-grow: 0;
`;

const ListItemButton = styled(Button)`
  margin-left: ${({ theme }) => theme.grid.units(0.5)};
`;

const DeviceDetails = styled.span`
  color: ${({ theme }) => theme.colors.notSelectable};
  margin-top: 0.05em;
  justify-self: flex-start;
`;

const formatIdentifiers = (identifiers: Identifiers) => {
  const { hwas, fingerprint } = identifiers;

  if (hwas) {
    // `hwas` is a string[]; formatHWADisplay coerces its argument via `${hwa}`
    // at runtime, so the array is stringified exactly as before.
    return formatHWADisplay(hwas as unknown as string);
  }
  if (fingerprint) {
    return fingerprint;
  }

  return null;
};

const getFlowJoVersion = (identifiers: Identifiers, productName?: string) => {
  if (productName !== 'flowJo') {
    return null;
  }

  const { hwas, fingerprint } = identifiers;
  if (hwas) {
    return 'FlowJo v10';
  }
  if (fingerprint) {
    return 'FlowJo v11';
  }

  return null;
};

type DeviceListProps = {
  devices: Device[];
  isRemovalDisabled: boolean;
  renameDevice: (device: Device) => (value: string) => void;
  handleRemoveClick: (device: Device) => void;
  handleCreateEotButtonClick: (device: Device) => void;
  handleExportEotButtonClick: (device: Device) => void;
  handleShowInfoEotModal: (device: Device) => void;
  productName?: string;
};

const DeviceList = ({
  devices,
  isRemovalDisabled,
  renameDevice,
  handleRemoveClick,
  handleCreateEotButtonClick,
  handleExportEotButtonClick,
  handleShowInfoEotModal,
  productName,
}: DeviceListProps) => {
  const hasOfflineToken = React.useMemo(
    () => devices.some((device) => device.extendedOfflineToken),
    [devices],
  );
  return (
    <List dataTestId="device-list" disabled={isRemovalDisabled}>
      {devices.map((device) => (
        <ListItem key={device.id} dataTestId={`device-list-item-${device.id}`}>
          <ListItemCell>
            <ListItemBlock>
              <DeviceNameRenameable value={device.name} onUpdate={renameDevice(device)} />
              {(() => {
                const version = getFlowJoVersion(device.identifiers, productName);
                return version ? <DeviceDetails>{version}</DeviceDetails> : null;
              })()}
              <DeviceDetails>Device ID: {formatIdentifiers(device.identifiers)}</DeviceDetails>
              <DeviceDetails>Created at: {formatDate(device.createdAt)}</DeviceDetails>
              <DeviceDetails>Last used: {formatDate(device.lastUsed)}</DeviceDetails>
            </ListItemBlock>
          </ListItemCell>
          <ListItemCell>
            {device.extendedOfflineToken && (
              <FjTooltip
                Component={<span>Activation Instructions</span>}
                showIcon
                onIconClick={() => handleShowInfoEotModal(device)}
              >
                <Button
                  buttonType={fjButtonTypes.LINK}
                  onClick={() => handleShowInfoEotModal(device)}
                >
                  <ListItemBlock>
                    <DeviceDetails>Extended Offline Token</DeviceDetails>
                    <DeviceDetails>
                      Start Date: {formatDate(device.extendedOfflineToken.startDate)}
                    </DeviceDetails>
                    <DeviceDetails>
                      End Date: {formatDate(device.extendedOfflineToken.endDate)}
                    </DeviceDetails>
                  </ListItemBlock>
                </Button>
              </FjTooltip>
            )}
          </ListItemCell>
          {device.extendedOfflineToken ? (
            <ListItemCell>
              <ListItemButton
                data-test={`export-eot-device-button-${device.id}`}
                disabled={false}
                onClick={() => handleExportEotButtonClick(device)}
              >
                Export Offline Token
              </ListItemButton>
            </ListItemCell>
          ) : (
            <ListItemCell>
              {!hasOfflineToken && (
                <ListItemButton
                  data-test={`request-eot-device-button-${device.id}`}
                  disabled={false}
                  onClick={() => handleCreateEotButtonClick(device)}
                >
                  Get Offline Token
                </ListItemButton>
              )}
            </ListItemCell>
          )}
          <ListItemButtonCell>
            <FjTooltip
              Component={<span>Devices with an Offline Token cannot be removed</span>}
              override={!device.extendedOfflineToken}
            >
              <ListItemButton
                data-test={`remove-device-button-${device.id}`}
                disabled={isRemovalDisabled || Boolean(device.extendedOfflineToken)}
                onClick={() => handleRemoveClick(device)}
              >
                Remove
              </ListItemButton>
            </FjTooltip>
          </ListItemButtonCell>
        </ListItem>
      ))}
    </List>
  );
};

export default DeviceList;
